import './ignoreExtensionErrors';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';
import './styles/storefront-theme.css';
import './i18n';
import App from './App';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import reportWebVitals from './reportWebVitals';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
  });
}

const isStorefrontBuild = process.env.REACT_APP_PUBLIC_SITE === 'true';
if (isStorefrontBuild) {
  document.documentElement.classList.add('storefront-site');
}

const AppToaster = ({ duration = 2000 }) => (
  <Toaster
    position="top-right"
    toastOptions={{
      duration,
      className: '',
      style: {
        background: 'var(--toast-bg)',
        color: 'var(--toast-color)',
        zIndex: 10000,
        cursor: 'pointer'
      }
    }}
  >
    {(t) => (
      <ToastBar toast={t}>
        {({ icon, message }) => (
          <div
            role="button"
            tabIndex={0}
            onClick={() => toast.dismiss(t.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toast.dismiss(t.id);
              }
            }}
            className="flex items-center gap-2"
            style={{ cursor: 'pointer' }}
            aria-label="Close notification"
          >
            {icon}
            {message}
          </div>
        )}
      </ToastBar>
    )}
  </Toaster>
);

const AppWrapper = ({ children }) => {
  if (isStorefrontBuild) {
    // Storefront build - no auth/notifications needed
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {children}
            <AppToaster duration={1800} />
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    );
  }

  // Regular CRM build - full providers
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <NotificationProvider>
            <BrowserRouter>
              {children}
              <AppToaster duration={2500} />
            </BrowserRouter>
          </NotificationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppWrapper>
        <App />
      </AppWrapper>
    </ErrorBoundary>
  </React.StrictMode>
);

const seoPrerender = document.getElementById('seo-prerender');
if (seoPrerender) {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isCrawler = /googlebot|bingbot|yandex|duckduckbot|baiduspider|facebookexternalhit|twitterbot|slurp|crawler|spider/i.test(ua);
  if (!isCrawler) {
    seoPrerender.setAttribute('hidden', '');
  }
}

reportWebVitals((metric) => {
  if (process.env.NODE_ENV !== 'production') return;
  if (process.env.REACT_APP_METRICS_ENDPOINT) {
    navigator.sendBeacon?.(process.env.REACT_APP_METRICS_ENDPOINT, JSON.stringify(metric));
    return;
  }
  fetch('/api/metrics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(metric),
    keepalive: true
  }).catch(() => {});
});